import { DialogForm, Label, useToast } from '@ais-ui/ui-core';
import { Job, useDeleteReport } from '@ais-ui/websockets';
import React, { useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { markErrorHandledLocally } from '@ais-ui/custom-hooks';

interface ReportDeleteProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  job: Job;
  onBackToReports: () => void;
}

export const ReportDelete = ({
  showDialog,
  setShowDialog,
  job,
  onBackToReports,
}: ReportDeleteProps) => {
  const { messages } = useLocalization();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate } = useDeleteReport();
  const { toast } = useToast();

  const onDelete = () => {
    setIsSubmitting(true);
    const reportName = job.name;

    if (!job.uuid) {
      toast({
        className: 'delete-report-error-toast',
        title: messages.genericToastTitleError,
        description: messages.reportsDeleteFailedNoIdMessage.replace(
          '{label}',
          reportName,
        ),
        variant: 'destructive',
      });
      setIsSubmitting(false);
      setShowDialog(false);
      return;
    }

    mutate(job.uuid, {
      onSuccess: () => {
        toast({
          className: 'delete-report-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.reportsDeleteSuccessMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
        onBackToReports();
      },
      onError: (error: any) => {
        markErrorHandledLocally(error);
        if (error.response?.status === 403) {
          toast({
            className: 'delete-report-error-toast',
            title: messages.genericToastTitleForbidden,
            description:
              messages.reportsDeleteFailedMissingPermissionsMessage.replace(
                '{label}',
                reportName,
              ),
            variant: 'destructive',
          });
          setIsSubmitting(false);
          setShowDialog(false);
        } else {
          toast({
            className: 'delete-report-error-toast',
            title: messages.genericToastTitleError,
            description: messages.reportsDeleteFailedGenericMessage.replace(
              '{label}',
              reportName,
            ),
            variant: 'destructive',
          });
          setIsSubmitting(false);
          setShowDialog(false);
        }
      },
    });
  };

  return (
    <DialogForm showDialog={showDialog} onOpenChange={setShowDialog} size="sm">
      <DialogForm.Header>
        <DialogForm.Title>{messages.reportsDeleteTitle}</DialogForm.Title>
        <DialogForm.Description>
          {messages.reportsDeleteWarning}
        </DialogForm.Description>
      </DialogForm.Header>
      <DialogForm.Body>
        <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
          <Label className="font-light">
            {messages.reportsDeleteNameLabel}
          </Label>
          <Label htmlFor="report-name">{job.name}</Label>
        </div>
      </DialogForm.Body>
      <DialogForm.Footer>
        <DialogForm.Cancel disabled={isSubmitting}>
          {messages.genericCancel}
        </DialogForm.Cancel>
        <DialogForm.Submit
          variant="destructive"
          id="report-confirm-delete-button"
          disabled={isSubmitting}
          onSubmit={onDelete}
        >
          {isSubmitting ? messages.genericDeleting : messages.genericDelete}
        </DialogForm.Submit>
      </DialogForm.Footer>
    </DialogForm>
  );
};
